import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { CanvasElementView } from './CanvasElementView';
import { AudioTrackPlayer } from './AudioTrackPlayer';
import { ReelCanvasDocument, isCanvasElement, isElementActiveAt, normalizeReelCanvas } from './reelCanvas.types';

export interface ReelPlaybackStageProps {
    width: number;
    height: number;
    canvas: unknown | null;
    /** Plays once from the start; `onEnded` fires when the reel duration is reached. */
    onEnded?: () => void;
    /** 0–100 screen volume applied on top of each audio layer's own volume. */
    volume?: number;
    muted?: boolean;
    /** Letterbox colour around the reel. */
    background?: string;
    /** Allow scaling above 100 % (signage screens are usually larger than the reel). */
    upscale?: boolean;
    /** `contain` letterboxes the reel; `cover` fills the stage and crops the overflow. */
    fit?: 'contain' | 'cover';
}

/**
 * Controls-free reel renderer that fills its container (letterboxed, aspect ratio kept) and plays the
 * reel exactly once. Same layer components as the editor and `ReelPlayer`, so a screen shows what the
 * editor shows. Remount it (change `key`) to play the next reel.
 */
export const ReelPlaybackStage: React.FC<ReelPlaybackStageProps> = ({
    width,
    height,
    canvas,
    onEnded,
    volume = 100,
    muted = false,
    background = '#000',
    upscale = true,
    fit = 'contain',
}) => {
    const doc: ReelCanvasDocument = useMemo(() => normalizeReelCanvas(canvas), [canvas]);
    const stageRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const [playheadMs, setPlayheadMs] = useState(0);
    const [playing, setPlaying] = useState(true);
    const endedRef = useRef(onEnded);
    endedRef.current = onEnded;

    useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const update = () => setStageSize({ width: el.clientWidth, height: el.clientHeight });
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const scale = useMemo(() => {
        if (stageSize.width <= 0 || stageSize.height <= 0) return 1;
        const sx = stageSize.width / width;
        const sy = stageSize.height / height;
        const fitted = fit === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
        return upscale ? fitted : Math.min(1, fitted);
    }, [stageSize, width, height, upscale, fit]);

    useEffect(() => {
        const c = canvasRef.current;
        const ctx = c?.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = doc.backgroundColor || '#000000';
        ctx.fillRect(0, 0, width, height);
    }, [doc.backgroundColor, width, height]);

    useEffect(() => {
        setPlayheadMs(0);
        setPlaying(true);
    }, [canvas]);

    useEffect(() => {
        if (!playing) return undefined;
        let raf = 0;
        let last = performance.now();
        let finished = false;
        const tick = (now: number) => {
            const dt = now - last;
            last = now;
            setPlayheadMs((p) => {
                const next = p + dt;
                if (next >= doc.durationMs) {
                    if (!finished) {
                        finished = true;
                        setPlaying(false);
                        setTimeout(() => endedRef.current?.(), 0);
                    }
                    return doc.durationMs;
                }
                return next;
            });
            if (!finished) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, doc.durationMs]);

    return (
        <Box
            ref={stageRef}
            sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Box sx={{ width: width * scale, height: height * scale, position: 'relative', flex: '0 0 auto' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width, height, transform: `scale(${scale})`, transformOrigin: 'top left', overflow: 'hidden' }}>
                    <canvas ref={canvasRef} width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, width, height, display: 'block' }} aria-hidden />
                    {doc.elements.map((el, index) =>
                        isCanvasElement(el) && isElementActiveAt(el, playheadMs) ? (
                            <CanvasElementView
                                key={el.id}
                                element={el}
                                zIndex={index + 1}
                                selected={false}
                                scale={scale}
                                canvas={{ width, height }}
                                readOnly
                                playheadMs={playheadMs}
                                playing={playing}
                                onSelect={() => undefined}
                                onChange={() => undefined}
                            />
                        ) : null,
                    )}
                </Box>
            </Box>
            {doc.elements.map((el) =>
                el.type === 'audio' ? (
                    <AudioTrackPlayer key={el.id} element={el} playheadMs={playheadMs} playing={playing} muted={muted} masterVolume={volume / 100} />
                ) : null,
            )}
        </Box>
    );
};

export default ReelPlaybackStage;
